import { Link } from "@tanstack/react-router"
import { Building2, ChevronDown, CircleHelp, LogOut, Settings, UserRound } from "lucide-react"

import { useLocalHref, useUi } from "@/core/i18n/locale-context"
import { displayName, initials } from "@/core/profile-display"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/dropdown-menu"

export interface ProfileMenuProps {
  name?: string | null
  email: string
  /** Picture of the user; the default avatar is the initials of the name. */
  avatarUrl?: string | null
  onSignOut: () => void
  isSigningOut?: boolean
  /** Narrow header (the marketing nav): the name shows from 1280 px, below that only the avatar. */
  compact?: boolean
}

/**
 * The account menu in the header of the marketing site and the signed-in app: avatar, name, and
 * one dropdown (Profile, Business, Settings, Help, Sign out). Radix handles Escape, focus return,
 * arrow keys and the menu roles. The three account entries are sections of one page, /account.
 */
export function ProfileMenu({
  name,
  email,
  avatarUrl,
  onSignOut,
  isSigningOut = false,
  compact = false,
}: ProfileMenuProps) {
  const ui = useUi().account
  const localHref = useLocalHref()
  const shown = displayName({ name, email })
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={ui.menu(shown)}
        className="hover:bg-muted focus-visible:ring-ring flex min-h-11 items-center gap-2 rounded-md px-2 text-sm font-medium outline-none focus-visible:ring-2 lg:min-h-9"
      >
        <span
          aria-hidden="true"
          className="bg-primary/15 text-primary grid size-8 shrink-0 place-items-center overflow-hidden rounded-full text-xs font-semibold"
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="size-full object-cover" />
          ) : (
            initials(shown)
          )}
        </span>
        <span
          aria-hidden="true"
          className={`max-w-40 truncate ${compact ? "max-xl:hidden" : "max-sm:hidden"}`}
        >
          {shown}
        </span>
        <ChevronDown
          className={`size-4 ${compact ? "max-xl:hidden" : "max-sm:hidden"}`}
          aria-hidden="true"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-60">
        <DropdownMenuLabel className="font-normal">
          <span className="block truncate text-sm font-medium">{shown}</span>
          <span className="text-muted-foreground block truncate text-xs">{email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild className="min-h-11 lg:min-h-8">
            <Link to="/account" hash="profile">
              <UserRound aria-hidden="true" />
              {ui.profile}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="min-h-11 lg:min-h-8">
            <Link to="/account" hash="business">
              <Building2 aria-hidden="true" />
              {ui.business}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="min-h-11 lg:min-h-8">
            <Link to="/account" hash="preferences">
              <Settings aria-hidden="true" />
              {ui.settings}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="min-h-11 lg:min-h-8">
            <a href={localHref("/contact")}>
              <CircleHelp aria-hidden="true" />
              {ui.help}
            </a>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="min-h-11 lg:min-h-8"
          disabled={isSigningOut}
          onSelect={() => onSignOut()}
        >
          <LogOut aria-hidden="true" />
          {ui.signOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
